import { io, Socket } from "socket.io-client";

import {
  apiBaseUrl,
  delay,
  getAccessToken,
  getTokenIsRefreshing,
  Log,
  ProfileStorage,
  wsPath,
} from "../helpers";
import {
  IClientToServerEvents,
  IServerToClientEvents,
  ISocketAuthPayload,
} from "../models";

const RECONNECT_DELAY_MS = 3000;
const WAIT_TIMEOUT_MS = 300;
const MAX_RECONNECT_ATTEMPTS = 5;

let socketInstance: Socket<
  IServerToClientEvents,
  IClientToServerEvents
> | null = null;
let currentAccessToken: string | null = null;

let isConnectionInProgress = false;
let reconnectTimer: NodeJS.Timeout | null = null;

const getWebSocketUrl = (): string => {
  const baseUrl = apiBaseUrl() || "http://localhost:3000";
  return baseUrl.replace(/\/+$/, "");
};

const createSocket = (token: string): Socket | null => {
  try {
    const socket = io(getWebSocketUrl(), {
      path: `/${wsPath()}`,
      transports: ["websocket"],
      autoConnect: false,
      withCredentials: true,
      auth: {
        accessToken: token,
        companyId: ProfileStorage.authSet?.companyId,
      } as ISocketAuthPayload,
    });

    Log.debug("WebSocket: Socket instance created");
    return socket;
  } catch (error) {
    Log.error("WebSocket: Failed to create socket instance", error);
    return null;
  }
};

const cleanupSocket = (): void => {
  if (socketInstance) {
    socketInstance.removeAllListeners();

    if (socketInstance.connected) {
      socketInstance.disconnect();
    }

    socketInstance = null;
  }

  currentAccessToken = null;

  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const establishConnection = async (
  token: string,
  isInitial = false,
): Promise<void> => {
  if (isConnectionInProgress) {
    Log.debug("WebSocket: Connection already in progress");
    return;
  }

  isConnectionInProgress = true;

  try {
    if (!socketInstance) {
      socketInstance = createSocket(token);

      if (!socketInstance) {
        throw new Error("Failed to create socket instance");
      }
    }

    if (socketInstance.connected && currentAccessToken === token) {
      Log.debug("WebSocket: Already connected with valid token");
      return;
    }

    if (socketInstance.connected) {
      Log.debug("WebSocket: Disconnecting existing connection");
      socketInstance.disconnect();
    }

    Log.log(`WebSocket: ${isInitial ? "Connecting" : "Reconnecting"}`);
    (socketInstance.auth as ISocketAuthPayload).accessToken = token;
    currentAccessToken = token;
    socketInstance.connect();
  } catch (error) {
    Log.error("WebSocket: Connection failed", error);
    throw error;
  } finally {
    isConnectionInProgress = false;
  }
};

const checkSocketConnection = async (
  newToken?: string,
  options?: { allowCookieAuth?: boolean },
): Promise<void> => {
  try {
    while (getTokenIsRefreshing()) {
      Log.debug("WebSocket: Waiting for token refresh...");
      await new Promise(resolve => setTimeout(resolve, WAIT_TIMEOUT_MS));
    }

    const bearer = newToken ?? getAccessToken();

    if (!bearer && !options?.allowCookieAuth) {
      Log.debug("WebSocket: No token available - cleaning up");
      cleanupSocket();
      return;
    }

    if (!bearer && options?.allowCookieAuth) {
      Log.debug("WebSocket: No bearer token — using HttpOnly session cookies");
    }

    const token = bearer ?? "";

    const isTokenChanged = token !== currentAccessToken;
    const isInitialConnection = !currentAccessToken;

    if (isTokenChanged || !socketInstance?.connected) {
      await establishConnection(token, isInitialConnection);
    }
  } catch (error) {
    Log.error("WebSocket: Connection check failed", error);
  }
};

export const checkSocketConnectionAsync = async (
  newToken?: string,
  options?: { allowCookieAuth?: boolean },
): Promise<boolean> => {
  await checkSocketConnection(newToken, options);

  if (!socketInstance) {
    Log.warnAndToast("WebSocket: No socket instance available");
    return false;
  }

  if (socketInstance.connected) {
    return true;
  }

  let attempts = 0;

  while (attempts < MAX_RECONNECT_ATTEMPTS) {
    const socket = socketInstance;

    try {
      await new Promise<void>((resolve, reject) => {
        const onConnect = (): void => {
          cleanupListeners();
          resolve();
        };

        const onConnectError = (error: Error): void => {
          cleanupListeners();
          reject(error);
        };

        const cleanupListeners = (): void => {
          socket?.off("connect", onConnect);
          socket?.off("connect_error", onConnectError);
        };

        socket?.once("connect", onConnect);
        socket?.once("connect_error", onConnectError);

        socket?.connect();
      });

      return true;
    } catch (error) {
      Log.warn("WebSocket: Connection error", error);
      attempts++;

      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        Log.warn(
          `WebSocket: Max connection attempts (${MAX_RECONNECT_ATTEMPTS}) reached`,
        );
        return false;
      }

      await delay(RECONNECT_DELAY_MS);
    }
  }

  return false;
};

export const getSocket = (): Socket<
  IServerToClientEvents,
  IClientToServerEvents
> | null => {
  if (!socketInstance) {
    Log.warnAndToast("Socket instance is not available");
    return null;
  }

  return socketInstance;
};
